import React from "react";

import { Theme } from "../../../theme/restyle/theme";
import { HStack, Text } from "../../primitive";

export type PillSize = "md" | "sm";

export type PillVariant = "rounded" | "outline";

export type PillColorScheme =
  | "primary"
  | "success"
  | "secondary"
  | "blue"
  | "purple"
  | "pink"
  | "warning"
  | "error";

export type PillProps = {
  label: string;
  size?: PillSize;
  variant?: PillVariant;
  colorScheme?: PillColorScheme;
  /** Element rendered to the left of the label. Takes no space when omitted. */
  leadingElement?: React.ReactNode;
  /** Element rendered to the right of the label. Takes no space when omitted. */
  trailingElement?: React.ReactNode;
  testID?: string;
};

type ThemeColor = keyof Theme["colors"];

const ROUNDED_BG: Record<PillColorScheme, ThemeColor> = {
  primary: "surface-main-primary-subtlest",
  success: "surface-status-success-subtlest",
  secondary: "surface-main-secondary-subtlest",
  blue: "surface-accent-blue-subtlest",
  purple: "surface-accent-purple-subtlest",
  pink: "surface-accent-pink-subtlest",
  warning: "surface-status-warning-subtlest",
  error: "surface-status-error-subtlest",
};

/**
 * Pill — a compact badge with optional leading and trailing elements.
 *
 * Variants: `rounded` (fully-rounded, coloured background) and `outline`
 * (white background with a border).
 * Sizes: `md` (medium) and `sm` (small).
 */
export const Pill = ({
  label,
  size = "md",
  variant = "rounded",
  colorScheme = "primary",
  leadingElement,
  trailingElement,
  testID,
}: PillProps) => {
  const isOutline = variant === "outline";
  const isMedium = size === "md";

  const backgroundColor: ThemeColor = isOutline
    ? "surface-primary"
    : ROUNDED_BG[colorScheme];

  const textVariant: keyof Theme["textVariants"] = isOutline
    ? "mainMicro"
    : isMedium
    ? "mainSmallSemiBold"
    : "mainTiny";

  // spacing tokens: 1=4px, 2=8px, 4=16px
  const gap = isMedium ? 2 : 1;
  const paddingX = isOutline ? 1 : isMedium ? 4 : 2;
  const paddingY = 1;

  return (
    <HStack
      testID={testID}
      backgroundColor={backgroundColor}
      alignItems="center"
      alignSelf="flex-start"
      gap={gap}
      px={paddingX}
      py={paddingY}
      borderWidth={isOutline ? 1 : 0}
      borderColor={isOutline ? "grayscale-500" : undefined}
      borderRadius={isOutline ? "1" : undefined}
      style={!isOutline ? { borderRadius: 100 } : undefined}
    >
      {leadingElement}
      <Text variant={textVariant} color="text-primary">
        {label}
      </Text>
      {trailingElement}
    </HStack>
  );
};
